export type LayoutEdgeLike = {
  source?: any;
  target?: any;
  sourceHandle?: any;
  targetHandle?: any;
  hidden?: any;
  data?: any;
};

type NormalizedLayoutEdge = {
  source: string;
  target: string;
};

type LayoutStepRef = {
  step: any;
  origId: string;
  treeOrder: number;
  parentStep: any | null;
};

export type DetachedLayoutGroup = {
  origIds: Set<string>;
  rootSteps: any[];
  steps: any[];
};

export type AttachedLayoutGroup = DetachedLayoutGroup & {
  anchorOrigIds: string[];
};

export type DetachedLayoutPlan = {
  mainActiveStepOrigIds: Set<string>;
  activeStepOrigIds: Set<string>;
  activeSteps: any[];
  allStepOrigIds: Set<string>;
  attachedGroups: AttachedLayoutGroup[];
  detachedGroups: DetachedLayoutGroup[];
};

export type RestoreDetachedLayoutFieldsOptions = {
  sourceRootSteps: any[];
  targetRootSteps: any[];
  edges: LayoutEdgeLike[] | undefined;
  changedOrigIds?: Set<string>;
};

export type CompactDetachedLayoutOptions = {
  horizontalGap?: number;
  routeVerticalGap?: number;
  startX?: number;
  startY?: number;
};

type LayoutBounds = {
  minY: number;
  maxY: number;
};

const normalizeKey = (value: any): string =>
  value !== undefined && value !== null ? String(value) : "";

const getStepOrigId = (step: any): string => normalizeKey(step?.origId);

const getNumericPosition = (value: any, fallback: number): number =>
  typeof value === "number" && !Number.isNaN(value) ? value : fallback;

const getStepY = (step: any): number =>
  getNumericPosition(step?.position?.y, getNumericPosition(step?.iy, 0) * 200);

const getLayoutBounds = (steps: any[]): LayoutBounds | null => {
  if (!Array.isArray(steps) || !steps.length) return null;

  return steps.reduce((bounds: LayoutBounds | null, step: any) => {
    const y = getStepY(step);
    if (!bounds) {
      return { minY: y, maxY: y };
    }

    return {
      minY: Math.min(bounds.minY, y),
      maxY: Math.max(bounds.maxY, y),
    };
  }, null);
};

const parseEdgeData = (edge: LayoutEdgeLike): any => {
  const { data } = edge || {};
  if (!data) return {};
  if (typeof data === "object") return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return {};
};

const isEdgeHidden = (edge: LayoutEdgeLike): boolean => {
  if (!edge) return true;
  if (edge.hidden) return true;
  return Boolean(parseEdgeData(edge).hidden);
};

const collectLayoutStepRefs = (rootSteps: any[]) => {
  const refs: LayoutStepRef[] = [];
  const refByOrigId = new Map<string, LayoutStepRef>();
  const origIdByAlias = new Map<string, string>();

  const rememberStep = (step: any, parentStep: any | null) => {
    const origId = getStepOrigId(step);
    if (!origId || refByOrigId.has(origId)) return;

    const ref = {
      step,
      origId,
      treeOrder: refs.length,
      parentStep,
    };

    refs.push(ref);
    refByOrigId.set(origId, ref);
    origIdByAlias.set(origId, origId);

    const id = normalizeKey(step?.id);
    if (id && !origIdByAlias.has(id)) {
      origIdByAlias.set(id, origId);
    }
  };

  const visitList = (list: any[], parentStep: any | null) => {
    if (!Array.isArray(list)) return;

    list.forEach((step) => {
      if (!step) return;
      rememberStep(step, parentStep);

      if (Array.isArray(step.routes)) {
        step.routes.forEach((route: any) => {
          visitList(route?.steps || [], step);
        });
      }

      if (Array.isArray(step.steps) && step.steps.length > 0) {
        visitList(step.steps, step);
      }
    });
  };

  visitList(rootSteps, null);

  return {
    refs,
    refByOrigId,
    origIdByAlias,
    allStepOrigIds: new Set(refs.map((ref) => ref.origId)),
  };
};

const normalizeLayoutEdges = (
  edges: LayoutEdgeLike[] | undefined,
  origIdByAlias: Map<string, string>
): NormalizedLayoutEdge[] => {
  if (!Array.isArray(edges)) return [];

  return edges.reduce<NormalizedLayoutEdge[]>((acc, edge) => {
    if (!edge || isEdgeHidden(edge)) return acc;

    const sourceRaw = normalizeKey(edge.source);
    const targetRaw = normalizeKey(edge.target);
    if (!sourceRaw || !targetRaw) return acc;

    const source =
      sourceRaw === "start" ? "start" : origIdByAlias.get(sourceRaw);
    const target =
      targetRaw === "start" ? "start" : origIdByAlias.get(targetRaw);
    if (!source || !target || source === target) return acc;

    acc.push({ source, target });
    return acc;
  }, []);
};

const buildVisibleConnections = (edges: NormalizedLayoutEdge[]) => {
  const connections = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    const sourceConnections = connections.get(edge.source) ?? new Set<string>();
    sourceConnections.add(edge.target);
    connections.set(edge.source, sourceConnections);

    const targetConnections = connections.get(edge.target) ?? new Set<string>();
    targetConnections.add(edge.source);
    connections.set(edge.target, targetConnections);
  });

  return connections;
};

const buildForwardConnections = (edges: NormalizedLayoutEdge[]) => {
  const forward = new Map<string, Set<string>>();

  edges.forEach((edge) => {
    const targets = forward.get(edge.source) ?? new Set<string>();
    targets.add(edge.target);
    forward.set(edge.source, targets);
  });

  return forward;
};

const getReachableStepOrigIds = (
  connections: Map<string, Set<string>>,
  allStepOrigIds: Set<string>
): Set<string> => {
  const startTargets = connections.get("start");

  if (!startTargets?.size) {
    return new Set(allStepOrigIds);
  }

  const reachable = new Set<string>();
  const queue = Array.from(startTargets);

  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (allStepOrigIds.has(current) && !reachable.has(current)) {
      reachable.add(current);
      const next = connections.get(current);

      if (next) {
        next.forEach((target) => {
          if (!reachable.has(target)) {
            queue.push(target);
          }
        });
      }
    }
  }

  return reachable;
};

const getActiveStepOrigIds = (
  edges: NormalizedLayoutEdge[],
  allStepOrigIds: Set<string>
): Set<string> => {
  if (!edges.length) {
    return new Set(allStepOrigIds);
  }
  return getReachableStepOrigIds(
    buildVisibleConnections(edges),
    allStepOrigIds
  );
};

const getMainActiveStepOrigIds = (
  edges: NormalizedLayoutEdge[],
  allStepOrigIds: Set<string>
): Set<string> => {
  if (!edges.length) {
    return new Set(allStepOrigIds);
  }
  return getReachableStepOrigIds(
    buildForwardConnections(edges),
    allStepOrigIds
  );
};

export function getVisibleConnectedStepOrigIds(
  rootSteps: any[],
  edges: LayoutEdgeLike[] | undefined,
  anchorOrigId: string | null | undefined = "start"
): Set<string> {
  const { origIdByAlias, allStepOrigIds } = collectLayoutStepRefs(rootSteps);
  const normalizedEdges = normalizeLayoutEdges(edges, origIdByAlias);

  if (!normalizedEdges.length) {
    return new Set(allStepOrigIds);
  }

  const connections = buildVisibleConnections(normalizedEdges);
  const normalizedAnchor =
    normalizeKey(anchorOrigId) === "start"
      ? "start"
      : origIdByAlias.get(normalizeKey(anchorOrigId));

  if (!normalizedAnchor) {
    return new Set();
  }

  if (normalizedAnchor === "start") {
    const startTargets = connections.get("start");
    if (!startTargets?.size) {
      return new Set(allStepOrigIds);
    }
  }

  const reachable = new Set<string>();
  const visited = new Set<string>();
  const queue =
    normalizedAnchor === "start"
      ? Array.from(connections.get("start") ?? [])
      : [normalizedAnchor];

  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (!visited.has(current)) {
      visited.add(current);

      if (allStepOrigIds.has(current)) {
        reachable.add(current);
      }

      const next = connections.get(current);
      if (next) {
        next.forEach((target) => {
          if (!visited.has(target)) {
            queue.push(target);
          }
        });
      }
    }
  }

  return reachable;
}

const buildDetachedAdjacency = (
  edges: NormalizedLayoutEdge[],
  detachedOrigIds: Set<string>
) => {
  const adjacency = new Map<string, Set<string>>();

  detachedOrigIds.forEach((origId) => {
    adjacency.set(origId, new Set<string>());
  });

  edges.forEach((edge) => {
    if (
      !detachedOrigIds.has(edge.source) ||
      !detachedOrigIds.has(edge.target)
    ) {
      return;
    }

    const sourceLinks = adjacency.get(edge.source);
    const targetLinks = adjacency.get(edge.target);
    if (sourceLinks) sourceLinks.add(edge.target);
    if (targetLinks) targetLinks.add(edge.source);
  });

  return adjacency;
};

const hasGroupAncestor = (
  ref: LayoutStepRef,
  groupOrigIds: Set<string>,
  refByOrigId: Map<string, LayoutStepRef>
): boolean => {
  let { parentStep } = ref;

  while (parentStep) {
    const parentOrigId = getStepOrigId(parentStep);
    if (parentOrigId && groupOrigIds.has(parentOrigId)) {
      return true;
    }

    parentStep = parentOrigId
      ? refByOrigId.get(parentOrigId)?.parentStep
      : null;
  }

  return false;
};

const collectLayoutGroups = (
  candidateOrigIds: Set<string>,
  refs: LayoutStepRef[],
  refByOrigId: Map<string, LayoutStepRef>,
  edges: NormalizedLayoutEdge[]
): DetachedLayoutGroup[] => {
  const adjacency = buildDetachedAdjacency(edges, candidateOrigIds);
  const orderedRefs = refs.filter((ref) => candidateOrigIds.has(ref.origId));
  const visited = new Set<string>();
  const groups: DetachedLayoutGroup[] = [];

  orderedRefs.forEach((ref) => {
    if (visited.has(ref.origId)) return;

    const groupOrigIds = new Set<string>();
    const queue = [ref.origId];

    while (queue.length > 0) {
      const current = queue.shift() as string;
      if (!visited.has(current)) {
        visited.add(current);
        groupOrigIds.add(current);

        const nextOrigIds = adjacency.get(current);

        if (nextOrigIds) {
          nextOrigIds.forEach((next) => {
            if (!visited.has(next)) {
              queue.push(next);
            }
          });
        }
      }
    }

    const groupRefs = refs
      .filter((item) => groupOrigIds.has(item.origId))
      .sort((first, second) => first.treeOrder - second.treeOrder);
    const rootGroupRefs = groupRefs.filter(
      (item) => !hasGroupAncestor(item, groupOrigIds, refByOrigId)
    );

    groups.push({
      origIds: groupOrigIds,
      rootSteps: rootGroupRefs.map((item) => item.step),
      steps: groupRefs.map((item) => item.step),
    });
  });

  return groups;
};

const getAnchorOrigIds = (
  groupOrigIds: Set<string>,
  mainActiveStepOrigIds: Set<string>,
  edges: NormalizedLayoutEdge[],
  refByOrigId: Map<string, LayoutStepRef>
): string[] => {
  const anchors = new Set<string>();

  edges.forEach((edge) => {
    if (
      groupOrigIds.has(edge.source) &&
      mainActiveStepOrigIds.has(edge.target)
    ) {
      anchors.add(edge.target);
    }
    if (
      groupOrigIds.has(edge.target) &&
      mainActiveStepOrigIds.has(edge.source)
    ) {
      anchors.add(edge.source);
    }
  });

  return Array.from(anchors).sort(
    (first, second) =>
      (refByOrigId.get(first)?.treeOrder ?? Number.MAX_SAFE_INTEGER) -
      (refByOrigId.get(second)?.treeOrder ?? Number.MAX_SAFE_INTEGER)
  );
};

export function createDetachedLayoutPlan(
  rootSteps: any[],
  edges: LayoutEdgeLike[] | undefined
): DetachedLayoutPlan {
  const { refs, refByOrigId, origIdByAlias, allStepOrigIds } =
    collectLayoutStepRefs(rootSteps);
  const normalizedEdges = normalizeLayoutEdges(edges, origIdByAlias);
  const mainActiveStepOrigIds = getMainActiveStepOrigIds(
    normalizedEdges,
    allStepOrigIds
  );
  const activeStepOrigIds = getActiveStepOrigIds(
    normalizedEdges,
    allStepOrigIds
  );
  const attachedOrigIds = new Set(
    Array.from(activeStepOrigIds).filter(
      (origId) => !mainActiveStepOrigIds.has(origId)
    )
  );
  const detachedOrigIds = new Set(
    Array.from(allStepOrigIds).filter(
      (origId) => !activeStepOrigIds.has(origId)
    )
  );
  const attachedGroups = collectLayoutGroups(
    attachedOrigIds,
    refs,
    refByOrigId,
    normalizedEdges
  ).map((group) => ({
    ...group,
    anchorOrigIds: getAnchorOrigIds(
      group.origIds,
      mainActiveStepOrigIds,
      normalizedEdges,
      refByOrigId
    ),
  }));
  const detachedGroups = collectLayoutGroups(
    detachedOrigIds,
    refs,
    refByOrigId,
    normalizedEdges
  );

  return {
    mainActiveStepOrigIds,
    activeStepOrigIds,
    activeSteps: refs
      .filter((ref) => activeStepOrigIds.has(ref.origId))
      .map((ref) => ref.step),
    allStepOrigIds,
    attachedGroups,
    detachedGroups,
  };
}

export function getUnplacedDetachedStepOrigIds(
  rootSteps: any[],
  edges: LayoutEdgeLike[] | undefined,
  verticalGap = 200
): Set<string> {
  const plan = createDetachedLayoutPlan(rootSteps, edges);
  const activeBounds = getLayoutBounds(plan.activeSteps);
  const unplacedOrigIds = new Set<string>();

  if (!activeBounds) return unplacedOrigIds;

  plan.detachedGroups.forEach((group) => {
    const groupBounds = getLayoutBounds(group.steps);
    if (!groupBounds) return;

    const isAlreadyInDetachedArea =
      groupBounds.maxY <= activeBounds.minY - verticalGap;

    if (!isAlreadyInDetachedArea) {
      group.origIds.forEach((origId) => unplacedOrigIds.add(origId));
    }
  });

  return unplacedOrigIds;
}

const LAYOUT_FIELD_NAMES = [
  "position",
  "ix",
  "iy",
  "index",
  "stepPosition",
  "conditionPosition",
] as const;

const cloneLayoutFieldValue = (value: any) => {
  if (!value || typeof value !== "object") return value;
  return JSON.parse(JSON.stringify(value));
};

const restoreLayoutFields = (sourceStep: any, targetStep: any) => {
  if (!sourceStep || !targetStep) return;

  LAYOUT_FIELD_NAMES.forEach((fieldName) => {
    if (sourceStep[fieldName] !== undefined) {
      targetStep[fieldName] = cloneLayoutFieldValue(sourceStep[fieldName]);
      return;
    }

    if (targetStep[fieldName] !== undefined) {
      delete targetStep[fieldName];
    }
  });
};

export function restoreDetachedLayoutFields({
  sourceRootSteps,
  targetRootSteps,
  edges,
  changedOrigIds = new Set<string>(),
}: RestoreDetachedLayoutFieldsOptions): void {
  if (!Array.isArray(sourceRootSteps) || !Array.isArray(targetRootSteps)) {
    return;
  }

  const sourceRefs = collectLayoutStepRefs(sourceRootSteps);
  const targetRefs = collectLayoutStepRefs(targetRootSteps);
  const plan = createDetachedLayoutPlan(targetRootSteps, edges);

  plan.detachedGroups.forEach((group) => {
    const containsChangedStep = Array.from(group.origIds).some((origId) =>
      changedOrigIds.has(origId)
    );

    if (containsChangedStep) return;

    group.origIds.forEach((origId) => {
      const sourceStep = sourceRefs.refByOrigId.get(origId)?.step;
      const targetStep = targetRefs.refByOrigId.get(origId)?.step;
      restoreLayoutFields(sourceStep, targetStep);
    });
  });
}

export function compactDetachedGroupLayout(
  rootSteps: any[],
  {
    horizontalGap = 200,
    routeVerticalGap = 200,
    startX = 0,
    startY = 0,
  }: CompactDetachedLayoutOptions = {}
): void {
  const compactList = (list: any[], baseX: number, baseY: number): number => {
    if (!Array.isArray(list) || list.length === 0) {
      return baseX;
    }

    let cursorX = baseX;
    let maxX = baseX;

    list.forEach((step) => {
      if (!step) return;

      const stepX = cursorX;
      step.position = {
        x: stepX,
        y: baseY,
      };

      let structureMaxX = stepX;

      if (Array.isArray(step.routes)) {
        step.routes.forEach((route: any, routeIndex: number) => {
          const routeMaxX = compactList(
            route?.steps || [],
            stepX,
            baseY + (routeIndex + 1) * routeVerticalGap
          );
          structureMaxX = Math.max(structureMaxX, routeMaxX);
        });
      }

      if (Array.isArray(step.steps) && step.steps.length > 0) {
        const childMaxX = compactList(step.steps, stepX, baseY);
        structureMaxX = Math.max(structureMaxX, childMaxX);
      }

      maxX = Math.max(maxX, structureMaxX);
      cursorX = structureMaxX + horizontalGap;
    });

    return maxX;
  };

  compactList(rootSteps, startX, startY);
}

export function withFilteredRouteSteps<T>(
  rootSteps: any[],
  includedOrigIds: Set<string>,
  callback: () => T
): T {
  const originalRoutes: Array<{ step: any; routes: any[] }> = [];

  const filterStepRoutes = (step: any) => {
    if (!step || !Array.isArray(step.routes)) return;

    originalRoutes.push({ step, routes: step.routes });
    step.routes = step.routes.map((route: any) => ({
      ...route,
      steps: Array.isArray(route?.steps)
        ? route.steps.filter((routeStep: any) =>
            includedOrigIds.has(getStepOrigId(routeStep))
          )
        : [],
    }));

    step.routes.forEach((route: any) => {
      route.steps.forEach((routeStep: any) => filterStepRoutes(routeStep));
    });
  };

  rootSteps.forEach((step) => filterStepRoutes(step));

  try {
    return callback();
  } finally {
    originalRoutes.reverse().forEach(({ step, routes }) => {
      step.routes = routes;
    });
  }
}
